import {
    isValidElement,
    type ReactNode,
    cloneElement,
    Children,
    type FC
} from "react";
import {
    View
} from "react-native";
import type ICardFooterProps from "./type";
import stylesheet, {
    CARD_FOOTER_ACTION_SKELETON_RATIO,
    CARD_FOOTER_TEXT_SKELETON_RATIO,
    CARD_FOOTER_ACTION_SIZES,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import type IButtonProps from "../../../button/type";
import type {
    CardPartContentProps
} from "../../type";
import {
    withCardContext
} from "../../context";
import Skeleton from "../../../skeleton";
import Button from "../../../button";

type CardFooterChildProps = Pick<IButtonProps, "spreadBehaviour" | "customTheme" | "style">;

const CardFooterContent: FC<CardPartContentProps<ICardFooterProps>> = ({
    customTheme: customThemeProp,
    variant = "plain",
    isDivided = true,
    align = "start",
    placeholder,
    children,
    context,
    style,
    ...props
}) => {
    const customTheme = customThemeProp ?? context.customTheme;

    const {
        controlHeights,
        shapeConfigs,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        container: containerDynamicStyle
    } = useStyles({
        inset: context.inset,
        shapeConfigs,
        isDivided,
        radiuses,
        borders,
        variant,
        colors,
        spaces,
        align
    });

    const actionHeight = controlHeights[CARD_FOOTER_ACTION_SIZES[context.size]];

    const renderActionSkeleton = (isStretched: boolean) => {
        return <Skeleton
            width={actionHeight * CARD_FOOTER_ACTION_SKELETON_RATIO}
            style={isStretched ? stylesheet.stretched : undefined}
            customTheme={customTheme}
            height={actionHeight}
            radius="actions"
            key="action"
        />;
    };

    const renderPlaceholder = (): ReactNode => {
        if(placeholder) {
            return placeholder;
        }

        if(align === "between") {
            return [
                <Skeleton
                    width={controlHeights.small * CARD_FOOTER_TEXT_SKELETON_RATIO}
                    typography="bodySmallSize"
                    customTheme={customTheme}
                    variant="text"
                    key="text"
                />,
                renderActionSkeleton(false)
            ];
        }

        if(align === "stretch") {
            return [
                renderActionSkeleton(true),
                <Skeleton
                    width={actionHeight * CARD_FOOTER_ACTION_SKELETON_RATIO}
                    style={stylesheet.stretched}
                    customTheme={customTheme}
                    height={actionHeight}
                    radius="actions"
                    key="second"
                />
            ];
        }

        return renderActionSkeleton(false);
    };

    const renderChildren = (): ReactNode => {
        return Children.map(children, (child) => {
            if(!isValidElement<CardFooterChildProps>(child)) {
                return child;
            }

            const isButton = child.type === Button;

            const isStretched = align === "stretch";

            if(!isButton && !isStretched) {
                return child;
            }

            return cloneElement(child, {
                ...(isButton ? {
                    spreadBehaviour: child.props.spreadBehaviour ?? "free",
                    customTheme: child.props.customTheme ?? customTheme
                } : {}),
                ...(isStretched ? {
                    style: [
                        child.props.style,
                        stylesheet.stretched
                    ]
                } : {})
            });
        });
    };

    return <View
        {...props}
        style={[
            style,
            stylesheet.container,
            context.isPressable ? stylesheet.inert : undefined,
            containerDynamicStyle
        ]}
    >
        {context.isLoading ? renderPlaceholder() : renderChildren()}
    </View>;
};
export default withCardContext("footer", CardFooterContent);
